import { FC, MouseEvent } from 'react';
import { useOutletContext } from 'react-router';

import { useTranslation, useLanguages } from '@app/app/i18n';
import { LocalizedLink, LocalizedNavLink } from '@app/shared/ui/localized-link';
import { route_path } from '@app/utils/route_path';
import { useLogOutMutation } from '@app/store/api/user.api';
import { CommonOutletContext } from '@app/app/layouts';
import {
  CogIcon,
  CreditCardIcon,
  CrownIcon,
  DownloadCloudIcon,
  LogoutIcon,
  MagicWandIcon,
  PickColorIcon,
  StarIcon,
  UserCircleIcon,
  UserIcon,
  UsersIcon,
  WorldIcon,
} from '@app/components/ui/icons';
import { cvaIcon } from '@app/shared/ui/elements';
import { Button } from '@app/shared/ui/button';
import { cn } from '@app/app/lib';
import { usePaymentsDialogStore } from '@app/widgets/payments-dialog';

import { isBoxedMode } from '@app/utils/user-status.utils';

import { LanguageSwitcher } from '../components/language-switcher';

import { useHeaderStore } from '../../model/header.store';

import { cvaMenuItem } from '../header-styles';

import { cvaButton, cvaBadge, cvaProfileEmail } from './header-popover-styles';
import { HeaderPopover } from './header-popover';

// Language switcher component
const LanguageSwitcherButton: FC = () => {
  const { currentLanguage, t: tHeader } = useTranslation('header');
  const languages = useLanguages('header');

  const currentLang = languages.find((lang) => lang.code === currentLanguage) || languages[0];

  return (
    <LanguageSwitcher
      itemTextSize="text-[14px]"
      trigger={
        <Button variant="secondary" size="text16" className={cvaButton()}>
          <WorldIcon className={cvaIcon()} />
          <span>{currentLang.flag}</span>
          <span>{tHeader('language.title')}</span>
        </Button>
      }
    />
  );
};

type AccountHeaderPopoverProps = { daysLeft: number | null; isTransparentStyles: boolean };

export const AccountHeaderPopover: FC<AccountHeaderPopoverProps> = ({
  isTransparentStyles,
  daysLeft,
}) => {
  const { t: tHeader } = useTranslation('header');
  const { setEmailNotify, setShowSettings, clearUserCtx } = useOutletContext<CommonOutletContext>();

  const { toggleHeaderMenuOpen } = useHeaderStore();
  const handleNavigationClick = () => toggleHeaderMenuOpen(false);

  const { setIsOpen: setShowPayments } = usePaymentsDialogStore();

  const [logoutQuery, { isLoading: isLoadingLogoutMutation }] = useLogOutMutation();

  const logOutHandler = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    logoutQuery().then(() => {
      handleNavigationClick();
      clearUserCtx();
      // navigate(route_path.main);
      // window.location.reload();
      window.location.href = route_path.main; //Need reload page after logout
    });
  };

  const { user_ctx, context, commonContext } = useOutletContext<CommonOutletContext>();

  const isBoxed = isBoxedMode(commonContext);
  const group = context?.group;
  const TAG = context?.TAG;
  const product_balance = context?.product_balance;

  return (
    <HeaderPopover
      Trigger={
        <button type="button" className={cvaMenuItem({ isTransparent: isTransparentStyles })}>
          <UserIcon className={cvaIcon()} />
          <span>{tHeader('account.account')}</span>
        </button>
      }
    >
      <div className="grid grid-cols-1 max-w-full color-white min-w-[15rem] ">
        {/* Email */}
        {user_ctx?.username && (
          <div className={cvaProfileEmail()}>
            {product_balance?.is_active &&
            !product_balance?.product.is_trial &&
            product_balance.type === 'subscription' ? (
              <CrownIcon className={cn(cvaIcon(), 'text-secondary_warning_SI_400_warning')} />
            ) : (
              <UserCircleIcon className={cvaIcon()} />
            )}

            <span className="truncate">{user_ctx.username}</span>
          </div>
        )}

        {/* Confirm email button */}
        {!user_ctx?.is_confirmed && (
          <button type="button" onClick={() => setEmailNotify(true)} className={cvaBadge()}>
            {tHeader('account.confirmEmail')}
          </button>
        )}

        {/* Days left*/}
        {daysLeft !== null && (
          <LocalizedNavLink to="/pricing/" className={cvaBadge()}>
            {daysLeft > 0
              ? tHeader('account.trialExpiresInDays', { days: daysLeft })
              : tHeader('account.trialExpiresToday')}
          </LocalizedNavLink>
        )}

        {/* Product expired alert */}
        {!product_balance?.is_active && product_balance?.product && (
          <LocalizedNavLink
            to="/pricing/"
            className={cvaBadge()}
            dangerouslySetInnerHTML={{
              __html: product_balance.product.is_trial
                ? tHeader('account.trialExpired')
                : tHeader('account.subscriptionExpired'),
            }}
          />
        )}

        {/* PayGo Product balance alert */}
        {product_balance?.is_active &&
          product_balance?.product &&
          !product_balance?.product.is_trial &&
          product_balance.type === 'presentation' && (
            <div className={cvaBadge()}>
              <DownloadCloudIcon className={cvaIcon()} />
              <span>
                {tHeader('account.downloads', {
                  count: product_balance.count,
                  total: product_balance.product.count,
                })}
              </span>
            </div>
          )}

        {/* Profile tags TODO: Check */}
        {Object.keys(TAG ?? {}).length > 0 && (
          <div className="">
            {!group ||
              !group[0] ||
              (group[0] === tHeader('account.admin') && (
                <StarIcon className={cvaIcon({ size: 'small' })} />
              ))}
            {group && group[0] ? <span>{group[0]}</span> : <span>{tHeader('account.owner')}</span>}
          </div>
        )}

        {/* Payments button */}
        {!isBoxed && (
          <Button
            variant="secondary"
            size="text16"
            onClick={() => setShowPayments(true)}
            className={cvaButton()}
            disabled={isLoadingLogoutMutation}
          >
            <CreditCardIcon className={cvaIcon()} />
            <span>
              {!product_balance?.is_active && product_balance?.product
                ? tHeader('account.upgradePlan')
                : tHeader('account.managePlan')}
            </span>
          </Button>
        )}

        {/* Settings button */}
        {
          <Button
            variant="secondary"
            size="text16"
            onClick={() => setShowSettings(true)}
            className={cvaButton()}
            disabled={isLoadingLogoutMutation}
          >
            <CogIcon className={cvaIcon()} />
            <span>{tHeader('account.settings')}</span>
          </Button>
        }

        {/* Language switcher */}
        <LanguageSwitcherButton />

        {/* Admin button */}
        {Object.keys(TAG ?? {}).length > 0 && (
          <>
            <Button
              variant="secondary"
              size="text16"
              className={cvaButton()}
              disabled={isLoadingLogoutMutation}
              asChild
            >
              <LocalizedLink to={route_path.team} className="inline-flex gap-x-1.5">
                <UsersIcon className={cvaIcon()} />
                <span>{tHeader('account.users')}</span>
              </LocalizedLink>
            </Button>

            <Button
              variant="secondary"
              size="text16"
              className={cvaButton()}
              disabled={isLoadingLogoutMutation}
              asChild
            >
              <LocalizedLink to={route_path.teamAttribute} className="inline-flex gap-x-1.5">
                <PickColorIcon className={cvaIcon()} />
                <span>{tHeader('account.corporateStyle')}</span>
              </LocalizedLink>
            </Button>
          </>
        )}
        {/* Logout button */}
        <Button
          variant="secondary"
          size="text16"
          onClick={logOutHandler}
          className={cvaButton()}
          disabled={isLoadingLogoutMutation}
        >
          <LogoutIcon className={cvaIcon()} />
          <span>{tHeader('auth.logout')}</span>
        </Button>
        {/* Admin button */}
        {user_ctx?.is_superuser && (
          <Button variant="secondary" size="text16" className={cvaButton()} asChild>
            <LocalizedLink
              to={route_path.admin}
              rel="noreferrer"
              target="_blank"
              className="inline-flex gap-x-1.5"
            >
              <MagicWandIcon className={cvaIcon()} />
              <span>{tHeader('account.admin')}</span>
            </LocalizedLink>
          </Button>
        )}
      </div>
    </HeaderPopover>
  );
};
